import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: 'Emma Richardson',
      position: 'CTO',
      company: 'TechFlow Solutions',
      location: 'San Francisco, USA',
      rating: 5,
      content: "Tripod Technologies transformed our legacy system into a modern, scalable platform. Their team's expertise in Java and cloud technologies exceeded our expectations. The project was delivered on time and within budget.",
      avatar: 'ER',
      bgColor: 'from-purple-500 to-pink-500'
    },
    {
      id: 2,
      name: 'Omar Al-Hassan',
      position: 'IT Director',
      company: 'Digital Innovations MENA',
      location: 'Dubai, UAE',
      rating: 5,
      content: "Outstanding service and technical expertise. The Tripod team handled our complex fintech requirements with professionalism. Their 24/7 support and communication made the collaboration seamless across different time zones.",
      avatar: 'OH',
      bgColor: 'from-cyan-500 to-teal-500'
    },
    {
      id: 3,
      name: 'James Mitchell',
      position: 'Head of Engineering',
      company: 'AlgoTrade Pro',
      location: 'London, UK',
      rating: 5,
      content: "Their algorithmic trading platform development was exceptional. The team understood our complex requirements and delivered a robust, high-performance solution. The Python expertise and AI integration capabilities are top-notch.",
      avatar: 'JM',
      bgColor: 'from-violet-500 to-purple-500'
    },
    {
      id: 4,
      name: 'Fatima Adebayo',
      position: 'Product Manager',
      company: 'HealthTech Innovations',
      location: 'Lagos, Nigeria',
      rating: 5,
      content: "The healthcare solution they built for us is incredible. HIPAA compliance, patient management, and telemedicine features were implemented flawlessly. The attention to detail in healthcare regulations was impressive.",
      avatar: 'FA',
      bgColor: 'from-emerald-500 to-cyan-500'
    },
    {
      id: 5,
      name: 'Klaus Weber',
      position: 'VP Technology',
      company: 'EuroSoft GmbH',
      location: 'Berlin, Germany',
      rating: 5,
      content: "Excellent cloud migration services. Tripod Technologies moved our entire infrastructure to AWS with zero downtime. Their DevOps expertise and automation capabilities saved us months of development time.",
      avatar: 'KW',
      bgColor: 'from-blue-500 to-indigo-500'
    },
    {
      id: 6,
      name: 'Marie Dubois',
      position: 'Founder',
      company: 'InnovateFrance SAS',
      location: 'Paris, France',
      rating: 5,
      content: "As a startup, we needed a reliable tech partner who could scale with us. Tripod Technologies delivered our MVP in record time and continues to support our growth with their flexible development approach.",
      avatar: 'MD',
      bgColor: 'from-pink-500 to-rose-500'
    }
  ];

  // Auto-rotation effect
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  // Get testimonials to display (3 at a time on desktop)
  const getVisibleTestimonials = () => {
    const testimonialsCopy = [...testimonials];
    const visible = [];
    
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push(testimonialsCopy[index]);
    }
    
    return visible;
  };

  return (
    <section 
      id="testimonials" 
      className="py-20 bg-gradient-to-br from-gray-50 via-purple-50/30 to-cyan-50/30 relative overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-72 h-72 border border-purple-200 rounded-full animate-spin-slow"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 border border-cyan-200 rounded-lg rotate-45 animate-pulse"></div>
        <div className="absolute top-1/2 right-1/3 w-32 h-32 border border-purple-300/50 rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Client <span className="bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text text-transparent animate-pulse">Testimonials</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Hear what our clients say about their experience working with Tripod Technologies
          </p>
        </div>

        {/* Desktop Rotating Testimonials */}
        <div className="hidden lg:block relative">
          <div className="grid grid-cols-3 gap-8 mb-8">
            {getVisibleTestimonials().map((testimonial, index) => (
              <Card
                key={`${testimonial.id}-${currentIndex}-${index}`}
                className="group hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 border-0 shadow-lg hover:-translate-y-3 transform-gpu bg-white/95 backdrop-blur-sm border border-cyan-200/50"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                <CardContent className="p-6 relative">
                  {/* Quote Icon */}
                  <div className="absolute top-4 right-4 opacity-20">
                    <Quote className="w-8 h-8 text-purple-400" />
                  </div>

                  {/* Avatar */}
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.bgColor} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                      {testimonial.avatar}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">{testimonial.name}</h4>
                      <p className="text-sm text-purple-600">{testimonial.position}</p>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center mb-4">
                    <div className="flex space-x-1 mr-2">
                      {renderStars(testimonial.rating)}
                    </div>
                    <span className="text-sm text-gray-600">({testimonial.rating}.0)</span>
                  </div>

                  {/* Content */}
                  <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                    "{testimonial.content}"
                  </p>

                  {/* Company & Location */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="font-medium text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-sm">{testimonial.company}</p>
                    <p className="text-xs text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={prevTestimonial}
              className="p-2 rounded-full bg-white shadow-lg border border-cyan-200 hover:bg-purple-50 transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5 text-purple-600" />
            </button>

            {/* Dots */}
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentIndex
                      ? 'bg-gradient-to-r from-purple-500 to-cyan-400 scale-125'
                      : 'bg-gray-300 hover:bg-purple-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-2 rounded-full bg-white shadow-lg border border-cyan-200 hover:bg-purple-50 transition-colors duration-200"
            >
              <ChevronRight className="w-5 h-5 text-purple-600" />
            </button>
          </div>
        </div>

        {/* Mobile Grid (Static) */}
        <div className="lg:hidden grid md:grid-cols-2 gap-6 mb-8">
          {testimonials.slice(0, 4).map((testimonial) => (
            <Card
              key={testimonial.id}
              className="group hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300 border-0 shadow-lg hover:-translate-y-3 transform-gpu bg-white/95 backdrop-blur-sm border border-cyan-200/50"
            >
              <CardContent className="p-6 relative">
                {/* Quote Icon */}
                <div className="absolute top-4 right-4 opacity-20">
                  <Quote className="w-8 h-8 text-purple-400" />
                </div>

                {/* Avatar */}
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.bgColor} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                    {testimonial.avatar}
                  </div>
                  <div className="ml-3">
                    <h4 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">{testimonial.name}</h4>
                    <p className="text-sm text-purple-600">{testimonial.position}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {renderStars(testimonial.rating)}
                  </div>
                  <span className="text-sm text-gray-600">({testimonial.rating}.0)</span>
                </div>

                {/* Content */}
                <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                  "{testimonial.content}"
                </p>

                {/* Company & Location */}
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-medium text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-sm">{testimonial.company}</p>
                  <p className="text-xs text-gray-500">{testimonial.location}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-cyan-200/50 p-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">98%</div>
              <div className="text-gray-600 text-sm">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-transparent bg-gradient-to-r from-cyan-500 to-purple-600 bg-clip-text mb-2">85+</div>
              <div className="text-gray-600 text-sm">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-violet-500 bg-clip-text mb-2">24/7</div>
              <div className="text-gray-600 text-sm">Support Available</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-transparent bg-gradient-to-r from-violet-500 to-cyan-500 bg-clip-text mb-2">4</div>
              <div className="text-gray-600 text-sm">Global Regions</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;